import {
	fetchFileRequest,
	makeRequest,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { KycResetScope } from '@dealstack/shared/lib/common/business/kyc';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import {
	PersonByEmailLookupRes,
	CreateKYCFileDto,
	CreateKYCFileViaKycAdminDto,
	DataDeclarationTct,
	DataKYCFileTct,
	InvestorKycFormRes,
	KycFileCertificationChoiceDto,
	KycFileCertificationRes,
	DataShareholderInvestorWithPersonRes,
	GetInvestmentDetailsRes,
	GetInvestorValuationsRes,
	GetSecurityVestingRes,
	GetShareholderCustomDetailRes,
	GetShareholderInvestmentEntryWithSDWDocumentRes,
	GetSignableContractsAndInvestorPhoneStatusRes,
	GetUserInvestmentDocumentsRes,
	GetUserInvestmentsListItemRes,
	GetStandaloneKycMetadataRes,
	PatchDeclarationsDto,
	PatchInvestmentSelectedBankAccountDto,
	PatchShareholderInvestorCompletionStepDto,
	PatchShareholderInvestorCustomDetailDto,
	PatchShareholderInvestorVehicleDataDto,
	PutShareholderInvestorDto,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { ServiceRequestOptions } from '@dealstack/frontend/shared/other/react/ApiWrapper/types';

export const getUserInvestmentsList = async (signal?: AbortSignal) => {
	const response = await makeRequest<ApiV1.Response<GetUserInvestmentsListItemRes[]>>(
		{
			endpoint: API_PATHS.SHAREHOLDER_INVESTMENT_USER_INVESTMENTS_GET,
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);

	return response;
};

export const getInvestorData = async (
	args?: { withDraftPersonRelations?: boolean; kycWorkflowId?: string } | void,
	options?: ServiceRequestOptions
) => {
	const { withDraftPersonRelations, kycWorkflowId } = args || {};
	const response = await makeRequest<ApiV1.Response<DataShareholderInvestorWithPersonRes>>(
		{
			endpoint: API_PATHS.SHAREHOLDER_INVESTOR_GET,
			method: HTTP_METHOD.GET,
			...((withDraftPersonRelations || kycWorkflowId) && {
				queryString: {
					...(withDraftPersonRelations && { withDraftPersonRelations }),
					...(kycWorkflowId && { kycWorkflowId }),
				},
			}),
		},
		{
			...options,
		}
	);

	return response;
};

export const updateInvestorData = async (
	args: PutShareholderInvestorDto,
	options?: ServiceRequestOptions
) => {
	await makeRequest<ApiV1.Response<unknown>>(
		{
			endpoint: API_PATHS.SHAREHOLDER_INVESTOR_PUT,
			method: HTTP_METHOD.PUT,
			body: JSON.stringify({
				...args,
			}),
		},
		{
			...options,
		}
	);
};

export const updateInvestorVehicleData = async (
	args: PatchShareholderInvestorVehicleDataDto | Record<string, never>,
	options?: ServiceRequestOptions
) => {
	await makeRequest<ApiV1.Response<unknown>>(
		{
			endpoint: API_PATHS.SHAREHOLDER_INVESTOR_VEHICLE_DATA_PATCH,
			method: HTTP_METHOD.PATCH,
			body: JSON.stringify({
				...args,
			}),
		},
		{
			...options,
		}
	);
};

export const lookupPersonByEmail = async (
	email: string,
	options?: ServiceRequestOptions
): Promise<PersonByEmailLookupRes> => {
	const response = await makeRequest<ApiV1.Response<PersonByEmailLookupRes>>(
		{
			endpoint: API_PATHS.PERSON_LOOKUP_BY_EMAIL_GET,
			method: HTTP_METHOD.GET,
			queryString: { email },
		},
		{
			...options,
		}
	);

	return (
		response.payload ?? {
			found: false,
			firstName: null,
			lastName: null,
			dateOfBirth: null,
			placeOfBirth: null,
			countryOfBirth: null,
			citizenship: null,
			address: null,
			taxResidences: null,
		}
	);
};

export const updateInvestorCompletionStep = async (
	args: PatchShareholderInvestorCompletionStepDto,
	options?: ServiceRequestOptions
) => {
	await makeRequest<ApiV1.Response<unknown>>(
		{
			endpoint: API_PATHS.SHAREHOLDER_INVESTOR_COMPLETION_STEP_PATCH,
			method: HTTP_METHOD.PATCH,
			body: JSON.stringify({
				...args,
			}),
		},
		{
			...options,
		}
	);
};

// Links the account entered on the bank-account step to the investment being joined/left.
export const linkInvestmentBankAccount = async (
	args: { investmentWorkflowId: string } & PatchInvestmentSelectedBankAccountDto,
	options?: ServiceRequestOptions
) => {
	const { investmentWorkflowId, ...body } = args;
	await makeRequest<ApiV1.Response<unknown>>(
		{
			endpoint: formatUnicorn(
				API_PATHS.SHAREHOLDER_INVESTMENT_INVESTMENTWORKFLOWID_SELECTED_BANK_ACCOUNT_PATCH,
				{ investmentWorkflowId }
			),
			method: HTTP_METHOD.PATCH,
			body: JSON.stringify(body),
		},
		{
			...options,
		}
	);
};

export const getInvestorCustomDetails = async (
	args?: { kycWorkflowId?: string } | void,
	options?: ServiceRequestOptions
) => {
	const kycWorkflowId = args?.kycWorkflowId;
	const endpoint = kycWorkflowId
		? `${API_PATHS.SHAREHOLDER_CUSTOM_DETAIL_GET}?kycWorkflowId=${encodeURIComponent(
				kycWorkflowId
			)}`
		: API_PATHS.SHAREHOLDER_CUSTOM_DETAIL_GET;

	const response = await makeRequest<ApiV1.Response<GetShareholderCustomDetailRes>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{
			...options,
		}
	);

	return response;
};

export const updateInvestorCustomDetails = async (
	args: PatchShareholderInvestorCustomDetailDto,
	options?: ServiceRequestOptions
) => {
	await makeRequest<ApiV1.Response<unknown>>(
		{
			endpoint: API_PATHS.SHAREHOLDER_CUSTOM_DETAIL_PUT,
			method: HTTP_METHOD.PUT,
			body: JSON.stringify({
				...args,
			}),
		},
		{
			...options,
		}
	);
};

export const getProofOfIdentity = async (
	kycWorkflowId: string,
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest<ApiV1.Response<DataKYCFileTct>>(
		{
			endpoint: API_PATHS.SHAREHOLDER_KYC_PROOF_OF_IDENTITY_GET,
			method: HTTP_METHOD.GET,
			queryString: { kycWorkflowId },
		},
		{
			...options,
		}
	);

	return response;
};

export const updateProofOfIdentity = async (
	args: Partial<CreateKYCFileDto> & { kycWorkflowId: string },
	options?: ServiceRequestOptions
) => {
	const { kycWorkflowId, ...body } = args;
	const response = await makeRequest<ApiV1.Response<DataKYCFileTct>>(
		{
			endpoint: API_PATHS.SHAREHOLDER_KYC_PROOF_OF_IDENTITY_PUT,
			method: HTTP_METHOD.PUT,
			body: JSON.stringify(body),
			queryString: { kycWorkflowId },
		},
		{
			...options,
		}
	);
	return response;
};

export const associateKycFileToInvestorViaMip = async (
	args: Partial<CreateKYCFileViaKycAdminDto>,
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest(
		{
			endpoint: API_PATHS.MIP_KYC_ASSOCIATE_KYC_FILE_PUT,
			method: HTTP_METHOD.PUT,
			body: JSON.stringify({
				...args,
			}),
		},
		{
			...options,
		}
	);
	return response;
};

export const deleteKycFileViaMip = async (
	{ fileId, userId }: { fileId: string; userId: string },
	options?: ServiceRequestOptions
) => {
	await makeRequest<ApiV1.Response<unknown>>(
		{
			endpoint: formatUnicorn(API_PATHS.MIP_KYC_KYC_FILE_ID_USERID_DELETE, {
				id: fileId,
				userId,
			}),
			method: HTTP_METHOD.DELETE,
		},
		{
			...options,
		}
	);
};

export const getProofOfAddress = async (kycWorkflowId: string, options?: ServiceRequestOptions) => {
	const response = await makeRequest<ApiV1.Response<DataKYCFileTct>>(
		{
			endpoint: API_PATHS.SHAREHOLDER_KYC_PROOF_OF_ADDRESS_GET,
			method: HTTP_METHOD.GET,
			queryString: { kycWorkflowId },
		},
		{
			...options,
		}
	);

	return response;
};

export const updateProofOfAddress = async (
	args: Partial<CreateKYCFileDto> & { kycWorkflowId: string },
	options?: ServiceRequestOptions
) => {
	const { kycWorkflowId, ...body } = args;
	const response = await makeRequest<ApiV1.Response<DataKYCFileTct>>(
		{
			endpoint: API_PATHS.SHAREHOLDER_KYC_PROOF_OF_ADDRESS_PUT,
			method: HTTP_METHOD.PUT,
			body: JSON.stringify(body),
			queryString: { kycWorkflowId },
		},
		{
			...options,
		}
	);
	return response;
};

export const getProofsOfPIV = async (kycWorkflowId: string, options?: ServiceRequestOptions) => {
	const response = await makeRequest<ApiV1.Response<DataKYCFileTct[]>>(
		{
			endpoint: API_PATHS.SHAREHOLDER_KYC_INVESTMENT_VEHICLE_GET,
			method: HTTP_METHOD.GET,
			queryString: { kycWorkflowId },
		},
		{
			...options,
		}
	);

	return response;
};

export const addProofOfPIV = async (
	args: Partial<CreateKYCFileDto> & { kycWorkflowId: string },
	options?: ServiceRequestOptions
) => {
	const { kycWorkflowId, ...body } = args;
	await makeRequest<ApiV1.Response<unknown>>(
		{
			endpoint: API_PATHS.SHAREHOLDER_KYC_INVESTMENT_VEHICLE_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify(body),
			queryString: { kycWorkflowId },
		},
		{
			...options,
		}
	);
};

export const setMorePivDocumentsStatus = async (kycWorkflowId: string) => {
	await makeRequest<ApiV1.Response<unknown>>({
		endpoint: API_PATHS.SHAREHOLDER_KYC_SET_OFF_MORE_PIV_DOCUMENTS_PATCH,
		method: HTTP_METHOD.PATCH,
		queryString: { kycWorkflowId },
	});
};

export const confirmKycFile = async (id: string) => {
	await makeRequest<ApiV1.Response<unknown>>({
		endpoint: formatUnicorn(API_PATHS.SHAREHOLDER_KYC_KYC_FILE_ID_CONFIRM_PATCH, { id }),
		method: HTTP_METHOD.PATCH,
	});
};

export const changeKycFileCertification = async ({
	kycFileId,
	choice,
}: {
	kycFileId: string;
	choice: KycFileCertificationChoiceDto;
}) => {
	return await makeRequest<ApiV1.Response<KycFileCertificationRes>>({
		endpoint: formatUnicorn(API_PATHS.SHAREHOLDER_KYC_KYC_FILE_ID_CERTIFICATION_PUT, {
			id: kycFileId,
		}),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(choice),
	});
};

export const resendKycFileCertification = async (kycFileId: string) => {
	return await makeRequest<ApiV1.Response<KycFileCertificationRes>>({
		endpoint: formatUnicorn(API_PATHS.SHAREHOLDER_KYC_KYC_FILE_ID_CERTIFICATION_RESEND_POST, {
			id: kycFileId,
		}),
		method: HTTP_METHOD.POST,
	});
};

export const confirmDeclaration = async (id: string) => {
	await makeRequest<ApiV1.Response<unknown>>({
		endpoint: formatUnicorn(API_PATHS.SHAREHOLDER_KYC_DECLARATION_ID_CONFIRM_PATCH, { id }),
		method: HTTP_METHOD.PATCH,
	});
};

export const resetInheritedKycItems = async (kycWorkflowId: string, scope?: KycResetScope) => {
	const queryString: Record<string, string> = { kycWorkflowId };
	if (scope) {
		queryString.scope = scope;
	}

	await makeRequest<ApiV1.Response<unknown>>({
		endpoint: API_PATHS.SHAREHOLDER_KYC_RESET_INHERITED_PATCH,
		method: HTTP_METHOD.PATCH,
		queryString,
	});
};

export const deleteProofOfPIV = async (
	{ fileId, kycWorkflowId }: { fileId: string; kycWorkflowId: string },
	options?: ServiceRequestOptions
) => {
	await makeRequest<ApiV1.Response<unknown>>(
		{
			endpoint: formatUnicorn(API_PATHS.SHAREHOLDER_KYC_ID_DELETE, { id: fileId }),
			method: HTTP_METHOD.DELETE,
			queryString: { kycWorkflowId },
		},
		{
			...options,
		}
	);
};

// The investor's KYC forms for a workflow, read from DocumentWorkflow bridges.
export const getDocumentWorkflowKycForms = async (
	kycWorkflowId: string,
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest<ApiV1.Response<InvestorKycFormRes[]>>(
		{
			endpoint: API_PATHS.SHAREHOLDER_KYC_KYC_FORMS_DOCUMENTWORKFLOW_GET,
			method: HTTP_METHOD.GET,
			queryString: { kycWorkflowId },
		},
		{
			...options,
		}
	);

	return response;
};

export const addFilledKycForm = async (
	args: Partial<CreateKYCFileDto> & { kycWorkflowId: string },
	options?: ServiceRequestOptions
) => {
	const { kycWorkflowId, ...body } = args;
	const response = await makeRequest<ApiV1.Response<DataKYCFileTct>>(
		{
			endpoint: API_PATHS.SHAREHOLDER_KYC_KYC_FORM_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify(body),
			queryString: { kycWorkflowId },
		},
		options
	);
	return response;
};

export const deleteFilledKycForm = async (
	{ fileId, kycWorkflowId }: { fileId: string; kycWorkflowId: string },
	options?: ServiceRequestOptions
) => {
	await makeRequest<ApiV1.Response<unknown>>(
		{
			endpoint: formatUnicorn(API_PATHS.SHAREHOLDER_KYC_ID_DELETE, { id: fileId }),
			method: HTTP_METHOD.DELETE,
			queryString: { kycWorkflowId },
		},
		{
			...options,
		}
	);
};

export const removeInvestorKycForm = async (
	{ templateId, kycWorkflowId }: { templateId: string; kycWorkflowId: string },
	options?: ServiceRequestOptions
) => {
	await makeRequest<ApiV1.Response<unknown>>(
		{
			endpoint: formatUnicorn(API_PATHS.SHAREHOLDER_KYC_KYC_FORM_TEMPLATEID_DELETE, {
				templateId,
			}),
			method: HTTP_METHOD.DELETE,
			queryString: { kycWorkflowId },
		},
		{
			...options,
		}
	);
};

export const preFillInvestorKycForm = async (
	{ templateId, kycWorkflowId }: { templateId: string; kycWorkflowId: string },
	options?: ServiceRequestOptions
) => {
	return await makeRequest<ApiV1.Response<InvestorKycFormRes>>(
		{
			endpoint: formatUnicorn(API_PATHS.SHAREHOLDER_KYC_KYC_FORM_TEMPLATEID_PREFILL_POST, {
				templateId,
			}),
			method: HTTP_METHOD.POST,
			queryString: { kycWorkflowId },
		},
		{
			...options,
		}
	);
};

export const confirmInvestorKycForm = async (
	{ templateId, kycWorkflowId }: { templateId: string; kycWorkflowId: string },
	options?: ServiceRequestOptions
) => {
	await makeRequest<ApiV1.Response<unknown>>(
		{
			endpoint: formatUnicorn(API_PATHS.SHAREHOLDER_KYC_KYC_FORM_TEMPLATEID_CONFIRM_PATCH, {
				templateId,
			}),
			method: HTTP_METHOD.PATCH,
			queryString: { kycWorkflowId },
		},
		{
			...options,
		}
	);
};

export const getDeclarations = async (kycWorkflowId: string, options?: ServiceRequestOptions) => {
	const response = await makeRequest<ApiV1.Response<DataDeclarationTct[]>>(
		{
			endpoint: API_PATHS.SHAREHOLDER_KYC_DECLARATIONS_GET,
			method: HTTP_METHOD.GET,
			queryString: { kycWorkflowId },
		},
		{
			...options,
		}
	);

	return response;
};

export const updateDeclarations = async (
	args: PatchDeclarationsDto & { kycWorkflowId: string },
	options?: ServiceRequestOptions
) => {
	const { kycWorkflowId, ...body } = args;
	const response = await makeRequest<ApiV1.Response<DataDeclarationTct[]>>(
		{
			endpoint: API_PATHS.SHAREHOLDER_KYC_DECLARATIONS_PATCH,
			method: HTTP_METHOD.PATCH,
			body: JSON.stringify(body),
			queryString: { kycWorkflowId },
		},
		{
			...options,
		}
	);

	return response;
};

export const getInvestmentSignableWorkflowContracts = async (
	args: {
		investmentWorkflowId?: string | null;
		investorId: string;
		excludeTsaContracts: boolean;
	},
	signal?: AbortSignal
) => {
	const { investorId, investmentWorkflowId, excludeTsaContracts } = args;
	const response = await makeRequest<
		ApiV1.Response<GetSignableContractsAndInvestorPhoneStatusRes>
	>(
		{
			endpoint: formatUnicorn(
				API_PATHS.SHAREHOLDER_CONTRACT_TRANSACTION_INVESTOR_INVESTORID_SIGNABLE_DOCUMENTS_GET,
				{
					investorId,
				}
			),
			method: HTTP_METHOD.GET,
			queryString: {
				excludeTsaContracts,
				investmentWorkflowId,
			},
		},
		{
			abortSignal: signal,
		}
	);

	return response;
};

export const reviewInvestorDocument = async ({
	documentWorkflowInstanceId,
	fileId,
}: {
	documentWorkflowInstanceId: string;
	fileId: string;
}) => {
	await makeRequest<ApiV1.Response<unknown>>({
		endpoint: formatUnicorn(
			API_PATHS.SHAREHOLDER_CONTRACT_REVIEW_DOCUMENT_WORKFLOW_DOCUMENTWORKFLOWINSTANCEID_POST,
			{
				documentWorkflowInstanceId,
			}
		),
		method: HTTP_METHOD.POST,
		queryString: { fileId },
	});
};

export const regenerateInvestmentSignableDocuments = async ({
	investorId,
	investmentWorkflowId,
	shouldRegenerateAllContracts,
}: {
	investorId: string;
	investmentWorkflowId?: string | null;
	shouldRegenerateAllContracts: boolean;
}) => {
	await makeRequest<ApiV1.Response<unknown>>({
		endpoint: formatUnicorn(
			API_PATHS.SHAREHOLDER_CONTRACT_TRANSACTION_INVESTOR_INVESTORID_GENERATE_SIGNABLE_DOCUMENTS_POST,
			{
				investorId,
			}
		),
		method: HTTP_METHOD.POST,
		queryString: {
			investmentWorkflowId,
			shouldRegenerateAllContracts,
		},
	});
};

export const downloadPrefillableFormFile = async (
	args: { fileId: string; instanceSignatureId: string },
	options?: ServiceRequestOptions
) => {
	const endpoint = formatUnicorn(
		API_PATHS.SHAREHOLDER_CONTRACT_DOWNLOAD_PREFILLABLE_FORM_FILE_FILEID_INSTANCESIGNATUREID_GET,
		{ fileId: args.fileId, instanceSignatureId: args.instanceSignatureId }
	);

	return await fetchFileRequest(endpoint, options);
};

export const inviteWitnessToSign = async ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	parentSignatureId,
	witnessEmail,
}: {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
	parentSignatureId: string;
	witnessEmail: string;
}) => {
	return makeRequest<ApiV1.Response<{ witnessSignatureId: string }>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_DOCUMENTWORKFLOWINSTANCEID_INVITE_WITNESS_POST,
			{ dealId, programmeId, documentWorkflowInstanceId }
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify({ parentSignatureId, witnessEmail }),
	});
};

export const inviteRepresentativeToSign = async ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	signatureId,
	representativeEmail,
}: {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
	signatureId: string;
	representativeEmail: string;
}) => {
	return makeRequest<ApiV1.Response<{ signatureId: string }>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_DOCUMENTWORKFLOWINSTANCEID_INVITE_REPRESENTATIVE_POST,
			{ dealId, programmeId, documentWorkflowInstanceId }
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify({ signatureId, representativeEmail }),
	});
};

export const resendSigningInvitation = async ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	signatureId,
}: {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
	signatureId: string;
}) => {
	return makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_DOCUMENTWORKFLOWINSTANCEID_RESEND_INVITATION_POST,
			{ dealId, programmeId, documentWorkflowInstanceId }
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify({ signatureId }),
	});
};

export const userInvestmentDocumentsQueryKey = [
	API_PATHS.SHAREHOLDER_INVESTOR_USER_INVESTMENT_DOCUMENTS_GET,
];

export const getUserInvestmentDocuments = async (
	_,
	signal?: AbortSignal
): Promise<GetUserInvestmentDocumentsRes[] | undefined> => {
	const response = await makeRequest<ApiV1.Response<GetUserInvestmentDocumentsRes[]>>(
		{
			endpoint: userInvestmentDocumentsQueryKey[0],
			method: HTTP_METHOD.GET,
		},
		{
			abortSignal: signal,
		}
	);

	return response.payload;
};

export const getSingleInvestorInvestmentWithSDWDocumentSummary = async (
	endpoint: string,
	signal?: AbortSignal
) => {
	const response = await makeRequest<
		ApiV1.Response<GetShareholderInvestmentEntryWithSDWDocumentRes>
	>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{
			abortSignal: signal,
		}
	);

	return response;
};

export const getInvestorInvestmentsWithSDWDocumentSummaries = async (
	endpoint: string,
	signal?: AbortSignal
) => {
	return await makeRequest<ApiV1.Response<GetShareholderInvestmentEntryWithSDWDocumentRes[]>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);
};

export const getInvestorValuations = async (endpoint: string, signal?: AbortSignal) => {
	return await makeRequest<ApiV1.Response<GetInvestorValuationsRes>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);
};

export const getAllInvestorsValuations = async (endpoint: string, signal?: AbortSignal) => {
	return await makeRequest<ApiV1.Response<GetInvestorValuationsRes[]>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);
};

export const getShareholderSecurityVestingData = async (
	endpoint: string,
	args: { investorId?: string; securityId?: string },
	signal?: AbortSignal
) => {
	const { investorId, securityId } = args;
	if (!investorId || !securityId) {
		return;
	}
	return await makeRequest<ApiV1.Response<GetSecurityVestingRes>>(
		{
			endpoint: formatUnicorn(endpoint, { investorId, securityId }),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);
};

export const getStandaloneKycMetadata = async (
	args: { kycWorkflowId: string },
	signal?: AbortSignal
) => {
	return await makeRequest<ApiV1.Response<GetStandaloneKycMetadataRes>>(
		{
			endpoint: formatUnicorn(API_PATHS.SHAREHOLDER_KYC_STANDALONE_KYCWORKFLOWID_GET, {
				kycWorkflowId: args.kycWorkflowId,
			}),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);
};

export const getInvestmentDetails = async (
	args: { mipId: string; investmentWorkflowId: string },
	signal?: AbortSignal
) => {
	return await makeRequest<ApiV1.Response<GetInvestmentDetailsRes>>(
		{
			endpoint: formatUnicorn(API_PATHS.SHAREHOLDER_INVESTMENT_BY_MIP_ID_MIPID_GET, {
				mipId: args.mipId,
			}),
			method: HTTP_METHOD.GET,
			queryString: { investmentWorkflowId: args.investmentWorkflowId },
		},
		{ abortSignal: signal }
	);
};
